(() => {
  if (customElements.get('video-premium-hero')) return;

  class VideoPremiumHero extends HTMLElement {
    constructor() {
      super();
      this.userChoice = null;
    }

    connectedCallback() {
      if (this.connection) return;
      this.mount = this.querySelector('[data-video-mount]');
      this.toggle = this.querySelector('[data-video-toggle]');
      if (!this.mount) return;

      this.connection = new AbortController();
      const options = { signal: this.connection.signal };
      this.desktopQuery = window.matchMedia('(min-width: 750px)');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isVisible = !('IntersectionObserver' in window);
      this.desktopQuery.addEventListener('change', () => this.mountVideo(), options);
      this.motionQuery.addEventListener('change', () => this.syncPlayback(), options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      this.toggle?.addEventListener('click', () => {
        if (!this.video) return;
        this.userChoice = this.video.paused && !this.pendingPlay ? 'play' : 'pause';
        this.syncPlayback();
      }, options);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.isVisible = entries.some((entry) => entry.isIntersecting);
          this.syncPlayback();
        });
        this.observer.observe(this);
      }

      this.mountVideo();
    }

    disconnectedCallback() {
      this.connection?.abort();
      this.connection = null;
      this.observer?.disconnect();
      this.observer = null;
      this.releaseVideo();
    }

    mountVideo() {
      const viewport = this.desktopQuery.matches ? 'desktop' : 'mobile';
      if (viewport === this.viewport && this.video) return;
      this.releaseVideo();
      this.viewport = viewport;
      const template = this.querySelector(`[data-video-template="${viewport}"]`);
      const video = template?.content.querySelector('video')?.cloneNode(true);
      if (!video) return;

      // Templates keep the inactive viewport's sources out of the live document.
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      this.video = video;
      this.mediaConnection = new AbortController();
      const options = { signal: this.mediaConnection.signal };
      video.addEventListener('play', () => this.updateControls(), options);
      video.addEventListener('playing', () => this.updateControls(), options);
      video.addEventListener('pause', () => this.updateControls(), options);
      video.addEventListener('ended', () => {
        this.userChoice = 'pause';
        this.updateControls();
      }, options);
      video.addEventListener('error', () => {
        this.userChoice = 'pause';
        this.syncPlayback();
      }, options);
      video.addEventListener('loadedmetadata', () => {
        if (video.videoWidth && video.videoHeight) {
          this.style.setProperty(`--hpc-ratio-${viewport}`, `${video.videoWidth} / ${video.videoHeight}`);
        }
      }, options);

      this.mount.replaceChildren(video);
      if (this.toggle) this.toggle.hidden = false;
      this.updateControls();
      this.syncPlayback();
    }

    releaseVideo() {
      this.pendingPlay = null;
      this.mediaConnection?.abort();
      this.mediaConnection = null;
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
        this.video.load();
        this.video.remove();
        this.video = null;
      }
      this.classList.remove('is-playing');
      if (this.toggle) this.toggle.hidden = true;
    }

    syncPlayback() {
      const video = this.video;
      if (!video || !this.isConnected) return;
      const wantsPlayback = this.userChoice === 'play' || (
        this.userChoice === null && this.dataset.autoplay === 'true' && !this.motionQuery.matches
      );
      if (!wantsPlayback || document.hidden || !this.isVisible) {
        // Invalidate pending requests before pause() can reject their promises.
        this.pendingPlay = null;
        video.pause();
        this.updateControls();
        return;
      }
      if (!video.paused || this.pendingPlay) return;

      const request = {};
      this.pendingPlay = request;
      const rejected = () => {
        if (this.video !== video || this.pendingPlay !== request) return;
        this.pendingPlay = null;
        this.userChoice = 'pause';
        this.updateControls();
      };
      try {
        Promise.resolve(video.play()).then(() => {
          if (this.video !== video || this.pendingPlay !== request) return;
          this.pendingPlay = null;
          this.updateControls();
        }, rejected);
      } catch (_) {
        rejected();
      }
    }

    updateControls() {
      const playing = Boolean(this.video && !this.video.paused && !this.video.ended);
      this.classList.toggle('is-playing', playing);
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-label', playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay);
      const playIcon = this.toggle.querySelector('[data-video-play]');
      const pauseIcon = this.toggle.querySelector('[data-video-pause]');
      if (playIcon) playIcon.hidden = playing;
      if (pauseIcon) pauseIcon.hidden = !playing;
    }
  }

  customElements.define('video-premium-hero', VideoPremiumHero);
})();
